import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { DspFunnelDescriptions, StrategyEx } from "@front/m19-models";
import { AccountSelectionService } from "@front/m19-services";
import { IIconComponent, ITooltipDirective, ModalService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  DspFunnelSelectorModalComponent,
  DspFunnelSelectorModalData,
} from "@m19-board/dsp/dsp-strategy-page/dsp-funnel-selector-modal.component";
import { StrategyInfoBlocComponent } from "@m19-board/shared/strategy-info-bloc/strategy-info-bloc.component";

@Component({
  selector: "app-strategy-page-dsp-funnel",
  imports: [StrategyInfoBlocComponent, TranslocoDirective, IIconComponent, ITooltipDirective],
  template: `@if (dspStrategy(); as dspStrategy) {
    <app-strategy-info-bloc
      [disabled]="isReadOnly()"
      (strategyInfoClick)="openFunnelSelectionModal()"
      *transloco="let t"
    >
      <div title class="text-sm text-gray-500 uppercase">
        {{ t("strategy-page.funnel") }}
      </div>
      <div body class="inline-flex items-center gap-2">
        <span class="font-medium">{{ t(DspFunnelDescriptions[dspStrategy.dspFunnel!].title) }}</span>
        <IIcon
          name="infoCircle"
          class="text-gray-500"
          [iTooltip]="t(DspFunnelDescriptions[dspStrategy.dspFunnel!].optimizationDescription)"
        />
      </div>
    </app-strategy-info-bloc>
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageDspFunnelComponent {
  private readonly modalService = inject(ModalService);
  private readonly accountMarketplaceSelection = inject(AccountSelectionService);
  private readonly translocoService = inject(TranslocoService);

  protected readonly DspFunnelDescriptions = DspFunnelDescriptions;

  strategy = input.required<StrategyEx>();

  dspStrategy = computed(() => {
    const strategy = this.strategy();
    return strategy.isDspStrategy() ? strategy : null;
  });

  accountMarketplace = toSignal(this.accountMarketplaceSelection.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  isReadOnly = toSignal(this.accountMarketplaceSelection.readOnlyMode$, { requireSync: true });
  organizationId = computed(() => this.accountMarketplace().resourceOrganizationId);

  openFunnelSelectionModal() {
    const strategy = this.strategy();
    if (!strategy || !strategy.isDspStrategy()) return;

    this.modalService.openModal<DspFunnelSelectorModalData, void>(DspFunnelSelectorModalComponent, {
      modalTitle: this.translocoService.translate("strategy-page.funnel_selection_modal_title"),
      data: {
        strategy: strategy,
        organizationId: this.organizationId(),
      },
    });
  }
}
